/**
 * Client-side search over the main page's provider → games tree.
 *
 * mode 'provider': keep providers whose name contains the query; their games
 * are left whole. mode 'game': keep providers that have a matching game, and
 * only the matching games under them; the provider's own name is ignored.
 * Case-insensitive, query trimmed, blank query returns the input untouched.
 * Never mutates the input: filtered providers are shallow copies.
 */
export function filterProviders(providers, query, mode = 'provider') {
    const needle = (query ?? '').trim().toLowerCase();
    const list = providers ?? [];
    if (needle === '') return list;

    const hit = (name) => (name ?? '').toLowerCase().includes(needle);

    if (mode === 'game') {
        return list
            .map((p) => ({ ...p, game_name: (p.game_name ?? []).filter((g) => hit(g.name)) }))
            .filter((p) => p.game_name.length > 0);
    }
    return list.filter((p) => hit(p.name));
}

/** A game with pictures to release: some exist and not all are out yet. */
export function isReadyToRelease(game) {
    return game.total_files_count > 0 && game.released_files_count < game.total_files_count;
}

/**
 * Keep only games ready to release, drop providers left empty. Each copy keeps
 * `games_total` (the count before filtering) for the "4 / 7" badge.
 */
export function filterReady(providers) {
    return (providers ?? [])
        .map((p) => ({
            ...p,
            games_total: p.games_total ?? (p.game_name ?? []).length,
            game_name: (p.game_name ?? []).filter(isReadyToRelease),
        }))
        .filter((p) => p.game_name.length > 0);
}

const lastUpdate = (p) => (p.game_name ?? [])
    .reduce((max, g) => (g.updated_at && g.updated_at > max ? g.updated_at : max), '');

/** sort: 'az' | 'za' | 'recent' (newest game or picture first). Returns a new array. */
export function sortProviders(providers, sort = 'az') {
    const list = [...(providers ?? [])];
    const byName = (a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' });
    if (sort === 'za') return list.sort((a, b) => byName(b, a));
    if (sort === 'recent') return list.sort((a, b) => lastUpdate(b).localeCompare(lastUpdate(a)) || byName(a, b));
    return list.sort(byName);
}

/**
 * Conflicts = open GUP disputes (869fheb1h); `summary` is getDisputesSummary().
 * 'all' leaves the list alone. 'providers-clean' drops every provider with a
 * dispute anywhere. 'games-conflict' / 'games-clean' keep only those games and
 * drop providers left empty; their copies carry `release_scope` for the
 * provider button: 'none' (conflicts are for sorting out, not releasing) or
 * 'shown' (release the visible games one by one, never the hidden ones).
 */
export function filterConflicts(providers, summary, mode = 'all') {
    const list = providers ?? [];
    if (mode === 'all') return list;

    const dirtyProviders = new Set();
    // Disputes the summary counts on the provider but on none of its games
    // (no game, or an id we can't match): no game can be proven clean there.
    const unplaced = new Set();
    const dirtyGames = new Set();
    for (const p of summary ?? []) {
        if (p.count > 0) dirtyProviders.add(String(p.id));
        let placed = 0;
        for (const g of p.games ?? []) if (g.count > 0) { dirtyGames.add(String(g.id)); placed += g.count; }
        if (p.count > placed) unplaced.add(String(p.id));
    }
    if (mode === 'providers-clean') return list.filter((p) => !dirtyProviders.has(String(p.id)));

    const wantDirty = mode === 'games-conflict';
    return list
        .map((p) => {
            const games = p.game_name ?? [];
            const kept = games.filter((g) => dirtyGames.has(String(g.id)) === wantDirty);
            return {
                ...p,
                games_total: p.games_total ?? games.length,
                game_name: kept,
                release_scope: wantDirty || unplaced.has(String(p.id)) ? 'none' : 'shown',
                hidden_conflicts: wantDirty ? 0 : games.length - kept.length,
            };
        })
        .filter((p) => p.game_name.length > 0);
}
